import { useAppDispatch, useAppSelector } from "../../app";
import { useCallback, useEffect } from "react";
import {
  EntityAnnotationBatchToBeNormalizedDto,
  EntityAnnotationDto,
  NormalizationErrorType,
} from "../../annotation/interfaces/annotation";
import { updateAnnotationNormalizationInState } from "../../annotation/store/annotationSlice";
import useAnnotationLoading from "../../annotation/hooks/useAnnotationLoading";
import { notification } from "antd";
import { useTranslation } from "react-i18next";
import { Annotation } from "../../annotator/interfaces/annotation";
import { mapToEntityAnnotationDto } from "../../annotation/utils/annotationMappers";
import { CategorizationAnnotation } from "../interfaces/category";
import { ConfigMap } from "../interfaces/configmap";
import { ERROR_WHILE_NORMALIZING } from "../utils/validationUtils";
import { useGetNormalizedAnnotationBatchMutation } from "../store/configMapApi";
import entityParentAnnotationsSelector, {
  ParentAnnotationsResponse,
} from "../selectors/entityParentAnnotationsSelector";
import { getCategorizationAnnotationForEntityAnnotation } from "../utils/entityCategorizationUtils";
import {
  initNormalizationBatchCoordinator,
  scheduleNormalization,
} from "../utils/normalizationBatchCoordinator";

const getCorrectParentValue = (
  parentAnnotations: Array<ParentAnnotationsResponse> | null,
  entityAnnotation: EntityAnnotationDto,
) => {
  if (!parentAnnotations) {
    return null;
  }

  const parentAnnotation = parentAnnotations?.find((pa) =>
    pa.childrenIds.includes(entityAnnotation.entityTypeId!),
  );

  if (!parentAnnotation) {
    return null;
  }

  if (parentAnnotation.normalizedValue) {
    return parentAnnotation.normalizedValue.value;
  }

  return parentAnnotation.value;
};

const useAnnotationNormalization = () => {
  const { t } = useTranslation("annotations");

  const dispatch = useAppDispatch();

  const metaData = useAppSelector(
    (state) => state.documentReducer.activeDocument?.metaData,
  );

  const documentSetId = useAppSelector(
    (state) => state.documentSetsReducer.activeDocumentSet?.id,
  );

  const configMap = useAppSelector((state) => state.configMapReducer.configMap);

  const categoryAnnotations = useAppSelector(
    (state) =>
      state.metadataReducer.categorizationState?.categorizationAnnotations,
  );

  const parentAnnotations = useAppSelector(entityParentAnnotationsSelector);

  const [getNormalizedAnnotationBatch] =
    useGetNormalizedAnnotationBatchMutation();

  const { setLoading } = useAnnotationLoading();

  const getCategoryValue = useCallback(
    (
      entityTypeId?: string,
      selectedValue?: string,
      initCategoryAnnotations?: Array<CategorizationAnnotation>,
      initConfigMap?: ConfigMap,
    ) => {
      let categoryAnnotationValue = selectedValue;
      const allCategoryAnnotations =
        initCategoryAnnotations ?? categoryAnnotations;
      // Callers that dispatch `setConfigMap` and normalize in the same tick
      // still read the previous render's value from Redux.
      const configMapToUse = initConfigMap ?? configMap;

      if (!selectedValue && entityTypeId) {
        // Use the utility function to get categorization annotation value
        categoryAnnotationValue =
          getCategorizationAnnotationForEntityAnnotation(
            configMapToUse,
            entityTypeId,
            1, // Default index
            allCategoryAnnotations || [],
          );
      }

      return categoryAnnotationValue;
    },
    [categoryAnnotations, configMap],
  );

  const getCategoryValueByAnnotation = useCallback(
    (
      entityAnnotation: EntityAnnotationDto,
      initCategoryAnnotations?: Array<CategorizationAnnotation>,
      initConfigMap?: ConfigMap,
    ) => {
      let categoryValue = entityAnnotation.categoryValue;

      if (!categoryValue) {
        categoryValue = getCategoryValue(
          entityAnnotation.entityTypeId,
          categoryValue,
          initCategoryAnnotations,
          initConfigMap,
        );

        return categoryValue;
      }
    },
    [getCategoryValue],
  );

  const normalizeEntityAnnotation = useCallback(
    async (entityAnnotation: EntityAnnotationDto): Promise<void> => {
      if (!entityAnnotation.entityNormalizations?.length) {
        return undefined;
      }
      entityAnnotation.categoryValue =
        getCategoryValueByAnnotation(entityAnnotation);
      entityAnnotation.parentValue = getCorrectParentValue(
        parentAnnotations,
        entityAnnotation,
      );

      const entityAnnotationsBody: EntityAnnotationBatchToBeNormalizedDto = {
        entityAnnotations: [entityAnnotation],
        metaData: metaData,
        documentSetId: documentSetId,
      };

      try {
        setLoading([entityAnnotation.id], true);

        const url =
          entityAnnotation.entityNormalizations[0].normalizationStrategy
            .urlBatch || null;

        const normalizedAnnotations = await getNormalizedAnnotationBatch({
          url,
          entityAnnotationsDto: entityAnnotationsBody,
        }).unwrap();

        const normalizedAnnotation = normalizedAnnotations[0];

        dispatch(
          updateAnnotationNormalizationInState({
            id: entityAnnotation.id,
            newValue: normalizedAnnotation.normalizedValue,
            method: normalizedAnnotation.normalizedMethod,
            options: normalizedAnnotation.options,
            errorMessage: normalizedAnnotation.errorMessage,
            multipleGroupBlocks: entityAnnotation.multipleGroupBlocks,
            isByUser: true,
            errorType: normalizedAnnotation.errorType,
          }),
        );
      } catch {
        notification.error({
          title: t("normalizationError"),
        });

        dispatch(
          updateAnnotationNormalizationInState({
            id: entityAnnotation.id,
            newValue: ERROR_WHILE_NORMALIZING,
            method: undefined,
            multipleGroupBlocks: entityAnnotation.multipleGroupBlocks,
            isByUser: true,
            errorType: NormalizationErrorType.ERROR_WHILE_NORMALIZING,
          }),
        );
      } finally {
        setLoading([entityAnnotation.id], false);
      }
    },
    [
      metaData,
      documentSetId,
      dispatch,
      t,
      getCategoryValueByAnnotation,
      getNormalizedAnnotationBatch,
      parentAnnotations,
      setLoading,
    ],
  );

  const normalizeEntityAnnotationsInBulk = useCallback(
    async (
      entityAnnotations: Array<EntityAnnotationDto>,
      searchCategoryValue: boolean = false,
      initCategoryAnnotations?: Array<CategorizationAnnotation>,
      initConfigMap?: ConfigMap,
    ) => {
      let entityAnnotationsToUse = entityAnnotations.filter((ea) => {
        const types = ea.entityNormalizations
          .map((en) => en.normalizationStrategy.type || "")
          .filter(Boolean);

        return !types.includes("csv");
      });

      if (!entityAnnotationsToUse.length) {
        return undefined;
      }

      if (searchCategoryValue) {
        entityAnnotationsToUse = [...entityAnnotationsToUse].map(
          (entityAnnotation) => {
            return {
              ...entityAnnotation,
              categoryValue: getCategoryValueByAnnotation(
                entityAnnotation,
                initCategoryAnnotations,
                initConfigMap,
              ),
            };
          },
        );
      }

      const entityAnnotationsBody: EntityAnnotationBatchToBeNormalizedDto = {
        entityAnnotations: entityAnnotationsToUse,
        metaData: metaData,
        documentSetId: documentSetId,
      };

      const toUpdateIds = entityAnnotationsToUse.map((ea) => ea.id);

      try {
        setLoading(toUpdateIds, true);

        const url =
          entityAnnotationsToUse[0].entityNormalizations[0]
            .normalizationStrategy.urlBatch || null;

        const normalizedAnnotations = await getNormalizedAnnotationBatch({
          url,
          entityAnnotationsDto: entityAnnotationsBody,
        }).unwrap();

        normalizedAnnotations.forEach((normalizedAnnotation) => {
          if (!normalizedAnnotation.id) {
            return;
          }

          const multipleGroupBlocks = entityAnnotations.find(
            (a) => a.id === normalizedAnnotation.id!,
          )!.multipleGroupBlocks;

          dispatch(
            updateAnnotationNormalizationInState({
              id: normalizedAnnotation.id,
              newValue: normalizedAnnotation.normalizedValue,
              method: normalizedAnnotation.normalizedMethod,
              options: normalizedAnnotation.options,
              errorMessage: normalizedAnnotation.errorMessage,
              multipleGroupBlocks: multipleGroupBlocks,
              isByUser: true,
              errorType: normalizedAnnotation.errorType,
            }),
          );
        });
      } catch {
        notification.error({
          title: t("normalizationError"),
        });

        entityAnnotationsToUse.forEach((entityAnnotation) => {
          dispatch(
            updateAnnotationNormalizationInState({
              id: entityAnnotation.id,
              newValue: ERROR_WHILE_NORMALIZING,
              method: undefined,
              multipleGroupBlocks: entityAnnotation.multipleGroupBlocks,
              isByUser: true,
              errorType: NormalizationErrorType.ERROR_WHILE_NORMALIZING,
            }),
          );
        });
      } finally {
        setLoading(toUpdateIds, false);
      }
    },
    [
      metaData,
      documentSetId,
      dispatch,
      t,
      getCategoryValueByAnnotation,
      getNormalizedAnnotationBatch,
      setLoading,
    ],
  );

  /**
   * Schedule a normalization request to be batched
   * This is the preferred method for normalizing annotations as it batches requests
   * instead of making individual API calls
   */
  const normalizeEntityAnnotationBatched = useCallback(
    (entityAnnotation: EntityAnnotationDto): void => {
      if (!entityAnnotation.entityNormalizations?.length) {
        return;
      }

      // Schedule this annotation for batched normalization
      scheduleNormalization(entityAnnotation);
    },
    [],
  );

  const normalizeIfAnnotationNeedsToBeNormalized = useCallback(
    (annotations: Array<Annotation>) => {
      // Collect all annotations that need normalization
      const annotationsToNormalize: EntityAnnotationDto[] = [];

      annotations.forEach((annotation) => {
        if (annotation.triggerNormalization) {
          const normalizedValue = {
            value:
              annotation.entityAnnotationNormalization?.normalizedValue || "",
            method:
              annotation.entityAnnotationNormalization?.normalizedMethod || "",
          };
          const entityAnnotationDto = mapToEntityAnnotationDto(
            annotation.id,
            annotation.values.join(" "),
            annotation.index || 1,
            annotation.entity.entityNormalizations ?? [],
            annotation.entity.id,
            annotation.multipleGroupBlocks,
          );
          annotationsToNormalize.push({
            ...entityAnnotationDto,
            normalizedValue,
          });
        }
      });

      // Schedule all collected annotations for batched normalization
      annotationsToNormalize.forEach((annotation) => {
        normalizeEntityAnnotationBatched(annotation);
      });
    },
    [normalizeEntityAnnotationBatched],
  );

  // Initialize batch coordinator with the bulk normalization function
  // This allows the coordinator to call normalizeEntityAnnotationsInBulk when the batch is ready
  useEffect(() => {
    initNormalizationBatchCoordinator((annotations: EntityAnnotationDto[]) => {
      // Enrich annotations with parent/category values before batching
      const enrichedAnnotations = annotations.map((annotation) => ({
        ...annotation,
        categoryValue: getCategoryValueByAnnotation(annotation),
        parentValue: getCorrectParentValue(parentAnnotations, annotation),
      }));

      normalizeEntityAnnotationsInBulk(enrichedAnnotations);
    });
  }, [
    normalizeEntityAnnotationsInBulk,
    getCategoryValueByAnnotation,
    parentAnnotations,
  ]);

  return {
    normalizeEntityAnnotation,
    normalizeEntityAnnotationsInBulk,
    normalizeIfAnnotationNeedsToBeNormalized,
    normalizeEntityAnnotationBatched, // New batched version
  };
};

export default useAnnotationNormalization;
